/* ==========================================================================
   KyoAir — Product Add-ons
   A <kyo-addons> custom element. Each row's "Add" control drops the variant
   into the cart and then turns into an inline quantity stepper, so the buyer
   can raise the quantity (e.g. to 34) without leaving the page. Uses the
   theme's own cart routes and pub/sub, but deliberately DOES NOT open the
   cart drawer — the card updates in place, quietly.

   Markup per control:
     <div class="kyc-addon-control" data-variant-id="..." data-min="1"
          data-step="1" data-max="">
       <button data-addon-add>...</button>
       <div class="kyc-addon-stepper">
         <button data-addon-minus>-</button>
         <input data-addon-qty>
         <button data-addon-plus>+</button>
       </div>
     </div>
   ========================================================================== */

if (!customElements.get('kyo-addons')) {
  customElements.define(
    'kyo-addons',
    class KyoAddons extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
      }

      get errorEl() {
        return this.querySelector('.kyc-addon-error');
      }

      onClick(event) {
        const addBtn = event.target.closest('[data-addon-add]');
        if (addBtn) {
          event.preventDefault();
          this.addToCart(addBtn.closest('.kyc-addon-control'));
          return;
        }
        const plus = event.target.closest('[data-addon-plus]');
        if (plus) {
          event.preventDefault();
          this.bump(plus.closest('.kyc-addon-control'), 1);
          return;
        }
        const minus = event.target.closest('[data-addon-minus]');
        if (minus) {
          event.preventDefault();
          this.bump(minus.closest('.kyc-addon-control'), -1);
        }
      }

      onChange(event) {
        const input = event.target.closest('[data-addon-qty]');
        if (!input) return;
        const control = input.closest('.kyc-addon-control');
        const value = parseInt(input.value, 10);
        this.setQuantity(control, isNaN(value) ? 0 : value);
      }

      // ---- helpers ----------------------------------------------------------

      variantId(control) {
        return control.dataset.variantId;
      }

      step(control) {
        return parseInt(control.dataset.step, 10) || 1;
      }

      min(control) {
        return parseInt(control.dataset.min, 10) || 1;
      }

      max(control) {
        const m = parseInt(control.dataset.max, 10);
        return isNaN(m) ? null : m;
      }

      currentQty(control) {
        const input = control.querySelector('[data-addon-qty]');
        return input ? parseInt(input.value, 10) || 0 : 0;
      }

      bump(control, direction) {
        const next = this.currentQty(control) + direction * this.step(control);
        this.setQuantity(control, next);
      }

      setInCart(control, inCart) {
        control.classList.toggle('is-in-cart', inCart);
      }

      setBusy(control, busy) {
        const stepper = control.querySelector('.kyc-addon-stepper');
        const addBtn = control.querySelector('[data-addon-add]');
        if (stepper) stepper.toggleAttribute('aria-busy', busy);
        if (addBtn) addBtn.toggleAttribute('aria-busy', busy);
        control._busy = busy;
      }

      qtyForVariant(cart, id) {
        if (!cart || !cart.items) return 0;
        const numericId = Number(id);
        return cart.items
          .filter((item) => item.variant_id === numericId || item.id === numericId)
          .reduce((sum, item) => sum + item.quantity, 0);
      }

      reflect(control, quantity) {
        const input = control.querySelector('[data-addon-qty]');
        if (input) input.value = quantity;
        this.setInCart(control, quantity > 0);
      }

      // publish so the header cart count refreshes, but never open the drawer
      broadcast(cart) {
        try {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kyo-addons', cart });
        } catch (error) {
          /* pub/sub not ready — the header will catch up on next navigation */
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      }

      async fetchCart() {
        const response = await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') });
        return response.json();
      }

      // ---- cart operations --------------------------------------------------

      addToCart(control) {
        if (!control || control._busy) return;
        const id = this.variantId(control);
        if (!id) return;

        this.showError(false);
        this.setBusy(control, true);

        const body = JSON.stringify({ items: [{ id: Number(id), quantity: this.min(control) }] });

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsed) => {
            if (parsed.status) {
              this.showError(parsed.description || parsed.message);
              return;
            }
            const cart = await this.fetchCart();
            this.reflect(control, this.qtyForVariant(cart, id) || this.min(control));
            this.broadcast(cart);
          })
          .catch((error) => console.error(error))
          .finally(() => this.setBusy(control, false));
      }

      setQuantity(control, requested) {
        if (!control || control._busy) return;
        const id = this.variantId(control);
        if (!id) return;

        let quantity = requested;
        const max = this.max(control);
        if (quantity < 0) quantity = 0;
        if (max !== null && quantity > max) quantity = max;

        this.showError(false);
        this.setBusy(control, true);

        const body = JSON.stringify({ id: String(id), quantity });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((cart) => {
            if (cart.status) {
              this.showError(cart.description || cart.message);
              return;
            }
            this.reflect(control, this.qtyForVariant(cart, id));
            this.broadcast(cart);
          })
          .catch((error) => console.error(error))
          .finally(() => this.setBusy(control, false));
      }

      showError(message) {
        const el = this.errorEl;
        if (!el) return;
        el.toggleAttribute('hidden', !message);
        if (message) el.textContent = message;
      }
    }
  );
}
